import { TransactionData } from '../domain/stream/transaction/TransactionDomains';
import { CustomFields, SuspiciousActivityReport } from '../domain/case/SuspiciousActivityReport';
import moment from 'moment';

class SarXmlConverter {
  private readonly transactions: TransactionData[];
  private customFields: CustomFields;
  private configuration: SuspiciousActivityReport;

  constructor(transactions: TransactionData[], customFields: CustomFields, configuration: SuspiciousActivityReport) {
    this.transactions = transactions;
    this.configuration = configuration;
    this.customFields = customFields;
  }

  public parse() {
    const yukumlu = this.parseYukumlu();
    const temelBilgiler = this.parseTemelBilgiler();
    const gercekKisiler = this.parseGercekKisi();
    const digerKisiler = this.parseDigerKisi();
    const supheliIslemler = this.parseSupheliIslemler();
    const hesaplar = this.parseHesaplar();
    const supheKategorileri = this.parseSupheKategorileri();
    const bildirimAciklama = this.parseBildirimAciklama();

    return `
      <sib>
        <yukumlu>${yukumlu}</yukumlu>
        <temelbilgiler>
          ${temelBilgiler}
        </temelbilgiler>
        <gercekkisiler>
          ${gercekKisiler}
        </gercekkisiler>
        <digerkisiler>
          ${digerKisiler}
        </digerkisiler>
        <supheliislemler>
          ${supheliIslemler}
        </supheliislemler>
        <hesaplar>
          ${hesaplar}
        </hesaplar>
        <suphekategorileri>
          ${supheKategorileri}
        </suphekategorileri>
        <bildirimaciklama>${bildirimAciklama}</bildirimaciklama>
      </sib>
    `;
  }

  private parseYukumlu() {
    const yukumlu = this.configuration.yukumlu;
    return yukumlu.staticVisible ? yukumlu.staticValue || '' : this.transactions[0].variables[yukumlu.value] || '';
  }

  private parseTemelBilgiler() {
    const temelBilgiler = this.configuration.temelBilgi.value;
    const getValue = (field: any) => (field.staticVisible ? field.staticValue || '' : this.transactions[0].variables[field.value] || '');
    return `
      <bildirimkategorisi>${getValue(temelBilgiler.bildirimKategorisi)}</bildirimkategorisi>
      <yerelreferansno>${getValue(temelBilgiler.yerelReferansNo)}</yerelreferansno>
      <oncekibildirimreferansno>${getValue(temelBilgiler.oncekiBildirimReferansNo)}</oncekibildirimreferansno>
      <aciliyet>${getValue(temelBilgiler.aciliyet)}</aciliyet>
      <terororgutu>${getValue(temelBilgiler.terorOrgutu)}</terororgutu>
    `;
  }

  private parseGercekKisi() {
    const gercekKisi: SuspiciousActivityReport['gercekKisi']['value'] = this.configuration.gercekKisi.value;
    const gercekKisiler: string[] = [];

    for (const transaction of this.transactions) {
      const getValue = (field: any) => (field.staticVisible ? field.staticValue || '' : transaction.variables[field.value] || '');
      const createTag = (tagName: string, value: any, isDateField: boolean = false) => {
        const content = getValue(value);
        const formattedContent = isDateField ? this.formatDate(content) : content;
        return formattedContent ? `<${tagName}>${formattedContent}</${tagName}>` : '';
      };

      const kimlikBelgesiXml = `
            ${createTag('belgeturu', gercekKisi.kimlikBelgesi.value.belgeTuru)}
            ${createTag('belgenumarasi', gercekKisi.kimlikBelgesi.value.belgeNumarasi)}
            ${createTag('gecerliliktarihi', gercekKisi.kimlikBelgesi.value.gecerlilikTarihi, true)}
        `.trim();
      const adresXml = `
                ${createTag('adressatir', gercekKisi.adres.value.adresSatir)}
                ${createTag('postakodu', gercekKisi.adres.value.postaKodu)}
                ${createTag('ilkodu', gercekKisi.adres.value.ilKodu)}
                ${createTag('ilcekodu', gercekKisi.adres.value.ilceKodu)}
                ${createTag('ulkekodu', gercekKisi.adres.value.ulkeKodu)}
        `.trim();
      const iletisimBilgisiiXml = createTag('iletisimbilgisi', gercekKisi.iletisimBilgisi);
      const kimlikBelgeleriXml = kimlikBelgesiXml ? `<kimlikbelgeleri>\n<kimlikbelgesi>\n${kimlikBelgesiXml}\n</kimlikbelgesi>\n</kimlikbelgeleri>` : '';
      const adreslerXml = adresXml ? `<adresler>\n<adres tip="string">\n${adresXml}\n</adres>\n</adresler>` : '';
      const iletisimBilgileriXml = iletisimBilgisiiXml ? `<iletisimbilgileri>\n${iletisimBilgisiiXml}\n</iletisimbilgileri>` : '';
      const gercekKisiXml = `
            ${createTag('islemlebaglantisi', gercekKisi.islemleBaglantisi)}
            ${createTag('islemlebaglantisiaciklama', gercekKisi.islemleBaglantisiAciklama)}
            ${createTag('adi', gercekKisi.adi)}
            ${createTag('soyadi', gercekKisi.soyadi)}
            ${createTag('tckn', gercekKisi.tckn)}
            ${createTag('vkn', gercekKisi.vkn)}
            ${createTag('ykn', gercekKisi.ykn)}
            ${createTag('dogumtarihi', gercekKisi.dogumTarihi, true)}
            ${createTag('dogumyeri', gercekKisi.dogumYeri)}
            ${createTag('anaadi', gercekKisi.anaAdi)}
            ${createTag('babaadi', gercekKisi.babaAdi)}
            ${createTag('uyrugu', gercekKisi.uyrugu)}
            ${createTag('cinsiyeti', gercekKisi.cinsiyeti)}
            ${kimlikBelgeleriXml}
            ${createTag('meslek', gercekKisi.meslek)}
            ${createTag('ekbilgi', gercekKisi.ekBilgi)}
            ${adreslerXml}
            ${iletisimBilgileriXml}
            ${createTag('kimlikbilgilerisahte', gercekKisi.kimlikBilgileriSahte)}
            ${createTag('magdur', gercekKisi.magdur)}
            ${createTag('kimliktespitturu', gercekKisi.kimlikTespitTuruTip)}
        `.trim();
      if (gercekKisiXml.trim()) {
        gercekKisiler.push(`
          <gercekkisi>
            ${gercekKisiXml}
          </gercekkisi>
        `);
      }
    }

    return gercekKisiler.join('\n');
  }

  private parseDigerKisi() {
    const digerKisi: SuspiciousActivityReport['digerKisi']['value'] = this.configuration.digerKisi.value;
    const digerKisiler: string[] = [];

    for (const transaction of this.transactions) {
      const getValue = (field: any) => (field.staticVisible ? field.staticValue || '' : transaction.variables[field.value] || '');
      const createTag = (tagName: string, value: any, isDateField: boolean = false) => {
        const content = getValue(value);
        const formattedContent = isDateField ? this.formatDate(content) : content;
        return formattedContent ? `<${tagName}>${formattedContent}</${tagName}>` : '';
      };
      const adresXml = `
            ${createTag('adressatir', digerKisi.adres.value.adresSatir)}
            ${createTag('postakodu', digerKisi.adres.value.postaKodu)}
            ${createTag('ilkodu', digerKisi.adres.value.ilKodu)}
            ${createTag('ilcekodu', digerKisi.adres.value.ilceKodu)}
            ${createTag('ulkekodu', digerKisi.adres.value.ulkeKodu)}
        `.trim();
      const iletisimBilgisiXml = createTag('iletisimbilgisi', digerKisi.iletisimBilgisi);
      const adreslerXml = adresXml ? `<adresler>\n<adres tip="string">\n${adresXml}\n</adres>\n</adresler>` : '';
      const iletisimBilgileriXml = iletisimBilgisiXml ? `<iletisimbilgileri>\n${iletisimBilgisiXml}\n</iletisimbilgileri>` : '';
      const digerKisiXml = `
            ${createTag('islemlebaglantisi', digerKisi.islemleBaglantisi)}
            ${createTag('islemlebaglantisiaciklama', digerKisi.islemleBaglantisiAciklama)}
            ${createTag('kurulusnevi', digerKisi.kurulusNevi)}
            ${createTag('unvan', digerKisi.unvan)}
            ${createTag('isletmeadi', digerKisi.isletmeAdi)}
            ${createTag('vkn', digerKisi.vkn)}
            ${createTag('sicilno', digerKisi.sicilNo)}
            ${createTag('faaliyetkonusu', digerKisi.faaliyetKonusu)}
            ${createTag('ulkekodu', digerKisi.ulkeKodu)}
            ${createTag('ilkodu', digerKisi.ilKodu)}
            ${createTag('ilcekodu', digerKisi.ilceKodu)}
            ${createTag('kurulustarihi', digerKisi.kurulusTarihi, true)}
            ${createTag('websitesi', digerKisi.websitesi)}
            ${createTag('ekbilgi', digerKisi.ekBilgi)}
            ${adreslerXml}
            ${iletisimBilgileriXml}
            ${createTag('kimlikbilgilerisahte', digerKisi.kimlikBilgileriSahte)}
            ${createTag('magdur', digerKisi.magdur)}
        `.trim();
      if (digerKisiXml) {
        digerKisiler.push(`
          <digerkisi>
            ${digerKisiXml}
          </digerkisi>
        `);
      }
    }

    return digerKisiler.join('\n');
  }

  private parseSupheliIslemler() {
    const supheliIslem: SuspiciousActivityReport['supheliIslem']['value'] = this.configuration.supheliIslem.value;
    const supheliIslemler: string[] = [];

    for (const transaction of this.transactions) {
      const getValue = (field: any) => (field.staticVisible ? field.staticValue || '' : transaction.variables[field.value] || '');
      const createTag = (tagName: string, value: any, isDateField: boolean = false) => {
        const content = getValue(value);
        const formattedContent = isDateField ? this.formatDate(content) : content;
        return formattedContent ? `<${tagName}>${formattedContent}</${tagName}>` : '';
      };
      const supheliIslemXml = `
            ${createTag('islemcesidi', supheliIslem.islemCesidi)}
            ${createTag('islemtarihi1', supheliIslem.islemTarihi1, true)}
            ${createTag('islemtarihi2', supheliIslem.islemTarihi2, true)}
            ${createTag('islemdurumu', supheliIslem.islemDurumu)}
            ${createTag('hesababagli', supheliIslem.hesabaBagli)}
            ${createTag('tutartp', supheliIslem.tutarTP)}
            ${createTag('tutar', supheliIslem.tutar)}
            ${createTag('parabirimi', supheliIslem.paraBirimi)}
            ${createTag('islemturu', supheliIslem.islemTuru)}
            ${createTag('islemkanali', supheliIslem.islemKanali)}
            ${createTag('ipv4', supheliIslem.ipv4)}
            ${createTag('port', supheliIslem.port)}
            ${createTag('islemaciklamasi', supheliIslem.islemAciklamasi)}
            ${createTag('ulkekodu', supheliIslem.ulkeKodu)}
            ${createTag('ilkodu', supheliIslem.ilKodu)}
            ${createTag('ilcekodu', supheliIslem.ilceKodu)}
            ${createTag('islemingerceklestirildigiyer', supheliIslem.isleminGerceklestigiYer)}
            ${createTag('islemintetiklendigiyer', supheliIslem.isleminTetiklendigiYer)}
            ${createTag('islemearaciliketmedurumu', supheliIslem.islemeAracilikEtmeDurumu)}
            ${createTag('islemearacilikedenkurum', supheliIslem.islemeAracilikEdenKurum)}
        `.trim();
      if (supheliIslemXml) {
        supheliIslemler.push(`
          <supheliislem>
            ${supheliIslemXml}
          </supheliislem>
        `);
      }
    }

    return supheliIslemler.join('\n');
  }

  private parseHesaplar() {
    const hesap: SuspiciousActivityReport['hesap']['value'] = this.configuration.hesap.value;
    const hesaplar: string[] = [];

    for (const transaction of this.transactions) {
      const getValue = (field: any) => (field.staticVisible ? field.staticValue || '' : transaction.variables[field.value] || '');
      const createTag = (tagName: string, value: any, isDateField: boolean = false) => {
        const content = getValue(value);
        const formattedContent = isDateField ? this.formatDate(content) : content;
        return formattedContent ? `<${tagName}>${formattedContent}</${tagName}>` : '';
      };
      const hesapSahibiXml = `
            ${createTag('magdur', hesap.hesapSahibi.value.magdur)}
            ${createTag('adsoyadunvan', hesap.hesapSahibi.value.adSoyadUnvan)}
            ${createTag('tckn', hesap.hesapSahibi.value.tckn)}
            ${createTag('vkn', hesap.hesapSahibi.value.vkn)}
            ${createTag('ykn', hesap.hesapSahibi.value.ykn)}
            ${createTag('pasaport', hesap.hesapSahibi.value.passaport)}
        `.trim();
      const hesapSahipleriXml = hesapSahibiXml ? `<hesapsahipleri>\n<hesapsahibi>\n${hesapSahibiXml}\n</hesapsahibi>\n</hesapsahipleri>` : '';
      const hesapXml = `
            ${createTag('sahisfirmasihesabi', hesap.sahisFirmasiHesabi)}
            ${createTag('hesapturu', hesap.hesapTuru)}
            ${createTag('hesapno', hesap.hesapNo)}
            ${createTag('iban', hesap.iban)}
            ${createTag('acilistarihi', hesap.acilisTarihi, true)}
            ${createTag('kapanistarihi', hesap.kapanisTarihi, true)}
            ${createTag('sonislemtarihi', hesap.sonIslemTarihi, true)}
            ${createTag('bakiyetp', hesap.bakiyeTP)}
            ${createTag('bakiye', hesap.bakiye)}
            ${createTag('parabirimi', hesap.paraBirimi)}
            ${createTag('kisitlamaturu', hesap.kisitlamaTuru)}
            ${createTag('kisitlamadigeraciklama', hesap.kisitlamaDigerAciklama)}
            ${hesapSahipleriXml}
        `.trim();
      if (hesapXml) {
        hesaplar.push(`
          <hesap>
            ${hesapXml}
          </hesap>
        `);
      }
    }

    return hesaplar.join('\n');
  }

  private parseSupheKategorileri() {
    const supheKategorisi = this.configuration.supheKategorisi;
    const supheKategorileri: string[] = [];

    for (const transaction of this.transactions) {
      const getValue = (field: any) => (field.staticVisible ? field.staticValue || '' : transaction.variables[field.value] || '');
      const value = getValue(supheKategorisi);
      if (value) {
        const xml = `<suphekategorisi>${value}</suphekategorisi>`;
        supheKategorileri.push(xml);
      }
    }

    return supheKategorileri.join('\n');
  }

  private parseBildirimAciklama() {
    const bildirimAciklama = this.configuration.bildirimAciklama;
    return bildirimAciklama.staticVisible ? bildirimAciklama.staticValue || '' : this.transactions[0].variables[bildirimAciklama.value] || '';
  }

  private formatDate(value: any): string {
    if (!value) return '';
    if (typeof value === 'number') {
      return moment(value).format('DD/MM/YYYY');
    }
    if (moment(value, true).isValid()) {
      return moment(value).format('DD/MM/YYYY');
    }
    return value;
  }
}

export default SarXmlConverter;
